import { useEffect, useState, type FormEvent, type ReactNode } from "react";

interface AuthStatus {
  enabled: boolean;
  authenticated: boolean;
}

interface PasswordGateProps {
  children: (session: { logout: () => Promise<void> }) => ReactNode;
}

async function readJson(response: Response): Promise<Record<string, unknown>> {
  try {
    return await response.json() as Record<string, unknown>;
  } catch {
    return {};
  }
}

export function PasswordGate({ children }: PasswordGateProps) {
  const [status, setStatus] = useState<AuthStatus>();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let disposed = false;
    void fetch("/__auth/status", { credentials: "same-origin" })
      .then(async (response) => {
        if (response.status === 404) return { enabled: false, authenticated: true };
        if (!response.ok) throw new Error("Không thể kiểm tra phiên đăng nhập.");
        return await readJson(response) as unknown as AuthStatus;
      })
      .then((next) => {
        if (!disposed) setStatus(next);
      })
      .catch((cause: unknown) => {
        if (!disposed) setError(cause instanceof Error ? cause.message : "Không thể kiểm tra phiên đăng nhập.");
      });
    return () => { disposed = true; };
  }, []);

  const login = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/__auth/login", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const result = await readJson(response);
      if (!response.ok) throw new Error(typeof result.error === "string" ? result.error : "Mật khẩu không đúng.");
      setPassword("");
      setStatus({ enabled: true, authenticated: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Đăng nhập thất bại.");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await fetch("/__auth/logout", { method: "POST", credentials: "same-origin" }).catch(() => undefined);
    setStatus((current) => current ? { ...current, authenticated: !current.enabled } : current);
  };

  if (error && !status) return <AuthMessage message={error} />;
  if (!status) return <AuthMessage message="Đang kiểm tra đăng nhập…" />;
  if (status.enabled && !status.authenticated) {
    return <main className="auth-shell">
      <section className="auth-card" aria-labelledby="auth-title">
        <p className="eyebrow">USB DEVICE WORKBENCH</p>
        <h1 id="auth-title">Đăng nhập</h1>
        <p className="muted">Nhập mật khẩu để sử dụng các chức năng ADB, Fastboot và Amlogic USB.</p>
        <form onSubmit={login}>
          <label className="field-label" htmlFor="app-password">Mật khẩu</label>
          <input id="app-password" className="text-input" type="password" autoComplete="current-password" autoFocus value={password} onChange={(event) => setPassword(event.target.value)} />
          {error && <p className="error-message" role="alert">{error}</p>}
          <button className="primary auth-submit" type="submit" disabled={!password || busy}>{busy ? "Đang xác thực…" : "Đăng nhập"}</button>
        </form>
      </section>
    </main>;
  }

  return <>{children({ logout })}</>;
}

function AuthMessage({ message }: { message: string }) {
  return <main className="auth-shell"><section className="auth-card"><p className="eyebrow">USB DEVICE WORKBENCH</p><h1>Đang khởi động</h1><p className="muted">{message}</p></section></main>;
}
